import { useEffect, useState } from "react";
import { X, Sparkles, ChevronLeft, ChevronRight, Pencil, Fuel, ShieldCheck, Share2, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * WhatsNew — one-shot release-notes carousel.
 *
 * Shows once per captain per release. We key the dismissal flag in localStorage
 * by RELEASE_TAG so bumping the tag (when shipping a new set of features) will
 * cause the carousel to fire again exactly once for everyone.
 *
 * To roll a new "What's new" experience:
 *   1. Bump RELEASE_TAG to a new string.
 *   2. Update RELEASE_LABEL + ITEMS.
 *
 * The final card is a "Bring a captain aboard" share card — the natural
 * closing beat. Uses navigator.share() where supported (mobile / iOS 12.1+
 * Safari / Chrome desktop 89+) with a clipboard fallback for older browsers.
 * The referral URL points at production so preview/TestFlight builds still
 * generate live-domain links.
 *
 * No backend, no per-user record — just localStorage. If a captain installs
 * fresh / clears storage they'll see it once more; that's fine.
 */
const RELEASE_TAG = "v1.5.5";
const RELEASE_LABEL = "What's new in v1.5.5";
const STORAGE_KEY = `bm_whats_new_seen_${RELEASE_TAG}`;

// Referral URL is always prod-domain so preview / native builds still generate
// live-share links. `signup_ref` is captured by refTracking.js on first hit
// and persisted on the new user's document for attribution (see
// /app/backend/routes/auth.py:_sanitize_ref).
const INVITE_BASE_URL = "https://bosunsmate.app";

const ITEMS = [
    {
        testid: "edit-delete",
        icon: Pencil,
        title: "Edit & delete any log entry",
        body: "Fat-fingered a fuel fill? Every entry — fuel, maintenance, parts, cruising log — now has a ⋯ menu with Edit and Delete. Deletes ask before they act, and nothing is ever removed without your say-so.",
    },
    {
        testid: "fuel-reconciliation",
        icon: Fuel,
        title: "Fuel stats that fix themselves",
        body: "Correct or remove an entry and your MPG, GPH and totals recalculate instantly from your real data — no more one bad entry haunting your season averages.",
    },
    {
        testid: "safety-compliance",
        icon: ShieldCheck,
        title: "Safety & insurance tracking",
        body: "The Safety tab tracks flare expiry, extinguisher service life and PFD counts against USCG minimums for your vessel — and the insurance tracker reminds you before renewal sneaks up.",
    },
    {
        testid: "invite-captain",
        icon: Share2,
        title: "Bring a captain aboard",
        body: "Know another skipper who'd love a proper logbook? Send them Bosun's Mate — every wheelhouse deserves a good bosun.",
        isShareCard: true,
    },
];

export default function WhatsNew() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [idx, setIdx] = useState(0);
    const [shareState, setShareState] = useState("idle"); // idle | copied | shared

    useEffect(() => {
        try {
            if (!localStorage.getItem(STORAGE_KEY)) {
                // Tiny delay so it doesn't fight with the dashboard's initial load.
                const t = setTimeout(() => setOpen(true), 900);
                return () => clearTimeout(t);
            }
        } catch {
            // localStorage unavailable (private mode etc.) — just show once per session
            setOpen(true);
        }
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* no-op */ }
        setOpen(false);
    };

    // Build the share URL. If we don't have a user id (edge case), fall back
    // to a plain link — still routable to the site, just no attribution.
    const inviteUrl = user?.id
        ? `${INVITE_BASE_URL}/?ref=${encodeURIComponent(user.id)}`
        : INVITE_BASE_URL;

    const shareInvite = async () => {
        // Any interaction with the share button counts as "seen" — even if the
        // captain closes the tab right after copying, we shouldn't re-nag on
        // next login.
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* no-op */ }
        const shareData = {
            title: "Bosun's Mate",
            text: "I've been using Bosun's Mate to log my boat — engine hours, fuel, cruising log, the lot. Thought you'd like it.",
            url: inviteUrl,
        };
        // navigator.share is available on mobile Safari, Chrome (Android +
        // desktop 89+), and Edge. Falls back to clipboard on Firefox etc.
        try {
            if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
                await navigator.share(shareData);
                setShareState("shared");
                setTimeout(dismiss, 500);
                return;
            }
        } catch (e) {
            // User cancelled the share sheet — that's normal, not an error.
            // Any other failure falls through to the clipboard path.
            if (e?.name === "AbortError") return;
        }
        // Clipboard fallback
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setShareState("copied");
        } catch {
            // As a last-ditch, open a new tab with the URL so the captain can
            // copy from the address bar. Rare — modern browsers all support
            // one of the two paths above.
            window.open(inviteUrl, "_blank", "noopener,noreferrer");
        }
    };

    if (!open) return null;

    const item = ITEMS[idx];
    const Icon = item.icon;

    return (
        <div
            data-testid="whats-new-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="whats-new-title"
            onClick={dismiss}
            style={{
                position: "fixed", inset: 0,
                background: "rgba(5, 15, 28, 0.78)",
                backdropFilter: "blur(8px)",
                display: "grid", placeItems: "center",
                zIndex: 200, padding: 16,
                animation: "bm-fade-in 0.25s ease",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bm-card"
                style={{
                    maxWidth: 460, width: "100%",
                    maxHeight: "calc(100vh - 32px)",
                    overflowY: "auto",
                    padding: 0,
                    border: "1px solid var(--bm-gold-dim)",
                    boxShadow: "0 24px 70px -20px rgba(0,0,0,0.6)",
                    background: "linear-gradient(135deg, rgba(212, 181, 114, 0.06), rgba(212, 181, 114, 0.01)), var(--bm-panel, #0c1828)",
                }}
            >
                <div style={{
                    padding: "22px 24px 14px",
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14,
                }}>
                    <div>
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                            color: "var(--bm-gold)", marginBottom: 6,
                        }}>
                            <Sparkles size={12} /> {RELEASE_LABEL}
                        </div>
                        <h2 id="whats-new-title" className="bm-serif" style={{
                            margin: 0, fontSize: 24, fontWeight: 500,
                        }}>
                            Fresh from the shipyard.
                        </h2>
                    </div>
                    <button
                        data-testid="whats-new-close-btn"
                        type="button"
                        onClick={dismiss}
                        className="bm-btn bm-btn-ghost"
                        aria-label="Close"
                        style={{ padding: "6px 8px" }}
                    >
                        <X size={16} />
                    </button>
                </div>

                <div
                    data-testid={`whats-new-item-${item.testid}`}
                    style={{
                        padding: "8px 28px 4px", textAlign: "center", minHeight: 220,
                    }}
                >
                    <div style={{
                        width: 56, height: 56, borderRadius: 14,
                        background: "linear-gradient(135deg, rgba(212,181,114,0.20), rgba(212,181,114,0.04))",
                        border: "1px solid rgba(212,181,114,0.35)",
                        display: "grid", placeItems: "center",
                        margin: "6px auto 16px", color: "var(--bm-gold)",
                    }}>
                        <Icon size={26} />
                    </div>
                    <div className="bm-serif" style={{
                        fontSize: 20, color: "var(--bm-text)", marginBottom: 10, fontWeight: 500,
                    }}>
                        {item.title}
                    </div>
                    <p style={{
                        margin: 0, color: "var(--bm-text-muted)", fontSize: 14,
                        lineHeight: 1.6, maxWidth: 360, marginInline: "auto",
                    }}>
                        {item.body}
                    </p>
                </div>

                <div style={{
                    display: "flex", justifyContent: "center", gap: 6,
                    margin: "18px 0 4px",
                }}>
                    {ITEMS.map((it, i) => (
                        <button
                            key={it.testid}
                            data-testid={`whats-new-dot-${i}`}
                            type="button"
                            onClick={() => setIdx(i)}
                            aria-label={`Go to card ${i + 1}`}
                            style={{
                                width: i === idx ? 22 : 6, height: 6, borderRadius: 999,
                                background: i === idx ? "var(--bm-gold)" : "rgba(212,181,114,0.25)",
                                border: "none", cursor: "pointer", padding: 0,
                                transition: "width 180ms ease, background 180ms ease",
                            }}
                        />
                    ))}
                </div>

                <div style={{
                    padding: "16px 20px 22px",
                    display: "flex", gap: 10, alignItems: "center",
                }}>
                    <button
                        data-testid="whats-new-prev-btn"
                        type="button"
                        onClick={() => setIdx((i) => Math.max(0, i - 1))}
                        disabled={idx === 0}
                        aria-label="Previous"
                        className="bm-btn bm-btn-ghost"
                        style={{
                            width: 44, height: 44, padding: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            opacity: idx === 0 ? 0.35 : 1,
                            cursor: idx === 0 ? "default" : "pointer",
                            flexShrink: 0,
                        }}
                    >
                        <ChevronLeft size={18} />
                    </button>
                    {item.isShareCard ? (
                        <button
                            data-testid="whats-new-share-btn"
                            type="button"
                            onClick={shareInvite}
                            className="bm-btn bm-btn-gold"
                            style={{
                                flex: 1, padding: "12px 18px",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            }}
                        >
                            {shareState === "copied" ? (
                                <>
                                    <Check size={16} />
                                    Link copied
                                </>
                            ) : shareState === "shared" ? (
                                <>
                                    <Check size={16} />
                                    Shared, thanks!
                                </>
                            ) : (
                                <>
                                    <Share2 size={16} />
                                    Share invite link
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            data-testid="whats-new-next-btn"
                            type="button"
                            onClick={() => setIdx((i) => Math.min(ITEMS.length - 1, i + 1))}
                            className="bm-btn bm-btn-gold"
                            style={{
                                flex: 1, padding: "12px 18px",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            }}
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes bm-fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
        </div>
    );
}
